import React, { useState, useEffect, useId } from 'react';
import { AlertCircle, Calendar, Clock, Pencil, Users } from 'lucide-react';
import { format, addMinutes } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import SearchableSelect from './SearchableSelect';

// Les deux boutons d'un pied de modal ont la même taille ET la même largeur.
// DESIGN-SYSTEM.md, section Boutons. Recopié de ExportTimetableModal.jsx, le
// seul écran du module qui était déjà aux conventions.
const BOUTON_PIED = 'h-9 min-w-[168px] justify-center gap-2';

const CHAMP = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm '
  + 'focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/20 transition-colors';

const EditTimeSlotModal = ({
  isOpen,
  onClose,
  onSubmit,
  event,
  classes,
  teachers,
  subjects,
  anneeScolaire
}) => {
  // Les bornes de l'année scolaire, pour que le calendrier du navigateur
  // refuse de lui-même une période hors de l'année - qui ne modifiait rien et
  // annonçait quand même un succès.
  //
  // ⚠️ Découpe de chaîne, JAMAIS `new Date()` : un objet Date traverse le
  // fuseau du navigateur et décale d'un jour. C'est la règle du dépôt.
  const borneDebutAnnee = anneeScolaire?.date_debut
    ? String(anneeScolaire.date_debut).slice(0, 10)
    : undefined;
  const borneFinAnnee = anneeScolaire?.date_fin
    ? String(anneeScolaire.date_fin).slice(0, 10)
    : undefined;

  const idDebut = useId();
  const idFin = useId();
  const idDateDebut = useId();
  const idDateFin = useId();

  const [formData, setFormData] = useState({
    enseignant_id: '',
    matiere_id: '',
    heure_debut: '',
    heure_fin: ''
  });

  const [scope, setScope] = useState('single');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  // Ce qui bloque l'envoi s'écrit DANS la page. Il y avait ici deux `alert()`
  // natifs : une boîte grise du système, bloquante, hors charte, et dans la
  // langue du navigateur.
  const [messageErreur, setMessageErreur] = useState(null);

  useEffect(() => {
    if (event) {
      setFormData({
        enseignant_id: event.teacherId?.toString() || '',
        matiere_id: event.subject_id?.toString() || '',
        heure_debut: format(new Date(event.date), 'HH:mm'),
        heure_fin: format(addMinutes(new Date(event.date), event.duration * 60), 'HH:mm')
      });

      setScope('single');
      setDateDebut('');
      setDateFin('');
      setMessageErreur(null);
    }
  }, [event]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessageErreur(null);

    if (scope === 'period' && (!dateDebut || !dateFin)) {
      setMessageErreur('Choisissez une date de début et une date de fin.');
      return;
    }

    // Comparaison de chaînes « AAAA-MM-JJ » : lexicographique, donc juste, et
    // aucun objet Date ne traverse un fuseau horaire au passage.
    if (scope === 'period' && dateDebut > dateFin) {
      setMessageErreur('La date de début doit précéder la date de fin.');
      return;
    }

    if (formData.heure_fin <= formData.heure_debut) {
      setMessageErreur("L'heure de fin doit être postérieure à l'heure de début.");
      return;
    }

    onSubmit({
      ...formData,
      enseignant_id: parseInt(formData.enseignant_id, 10),
      matiere_id: parseInt(formData.matiere_id, 10),
      classe_id: event.classId,
      affectation_id: event.affectation_id,
      jour_semaine: new Date(event.date).getDay() || 7,
      salle: event.salle || '',
      scope,
      ...(scope === 'period' && {
        date_debut: dateDebut,
        date_fin: dateFin
      })
    });
  };

  const handleClose = () => {
    setScope('single');
    setDateDebut('');
    setDateFin('');
    setMessageErreur(null);
    onClose();
  };

  if (!event) return null;

  const scopeOptions = [
    {
      value: 'single',
      icon: Calendar,
      title: 'Ce créneau uniquement',
      description: `Le créneau du ${format(new Date(event.date), 'dd/MM/yyyy')}, et lui seul`
    },
    {
      value: 'all',
      icon: Users,
      title: "Tous les créneaux de l'année",
      description: 'Tous les créneaux identiques : même jour, même heure, même classe'
    },
    {
      value: 'period',
      icon: Clock,
      title: 'Période personnalisée',
      description: 'Une plage de dates à choisir'
    }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(ouvert) => { if (!ouvert) handleClose(); }}>
      <DialogContent
        className="w-[95vw] max-w-5xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0"
        // Une saisie est en cours : un clic à côté ne doit pas l'emporter.
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
            <Pencil size={18} />
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Modifier le créneau
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              {event.title || 'Créneau'} - {format(new Date(event.date), 'EEEE dd/MM/yyyy')}
            </DialogDescription>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          {/* Le corps, seule zone qui défile. Format large plutôt que long :
              la portée à gauche, les nouvelles valeurs à droite. */}
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
              <div>
                <h3 className="mb-3 text-sm font-semibold text-gray-700">
                  Portée de la modification
                </h3>

                <div className="grid grid-cols-1 gap-3">
                  {scopeOptions.map((option) => {
                    const Icon = option.icon;
                    const isSelected = scope === option.value;

                    return (
                      <div key={option.value}>
                        <label
                          className={`relative flex cursor-pointer items-start rounded-lg border p-4 transition-all ${isSelected
                            ? 'border-[#246990] bg-[#246990]/5'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`}
                        >
                          <div className="relative mt-0.5 flex items-center">
                            <input
                              type="radio"
                              name="scope"
                              value={option.value}
                              checked={isSelected}
                              onChange={(e) => setScope(e.target.value)}
                              className="sr-only"
                            />
                            <div className={`flex h-5 w-5 items-center justify-center rounded-full border-2 transition-all ${isSelected
                              ? 'border-[#246990] bg-[#246990]'
                              : 'border-gray-300'}`}
                            >
                              {isSelected && <div className="h-2 w-2 rounded-full bg-white" />}
                            </div>
                          </div>

                          <div className={`mx-4 rounded-lg p-2 ${isSelected
                            ? 'bg-[#246990] text-white'
                            : 'bg-gray-100 text-gray-600'}`}
                          >
                            <Icon size={18} />
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className={`mb-0.5 font-semibold ${isSelected ? 'text-[#246990]' : 'text-gray-900'}`}>
                              {option.title}
                            </div>
                            <div className="text-sm text-gray-600">{option.description}</div>
                          </div>
                        </label>

                        {option.value === 'period' && isSelected && (
                          <div className="mt-3 grid grid-cols-2 gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <div>
                              <label htmlFor={idDateDebut} className="mb-2 block text-xs font-semibold text-gray-700">
                                Date de début
                              </label>
                              <input
                                id={idDateDebut}
                                type="date"
                                value={dateDebut}
                                onChange={(e) => setDateDebut(e.target.value)}
                                min={borneDebutAnnee}
                                max={dateFin || borneFinAnnee}
                                className={CHAMP}
                                required
                              />
                            </div>
                            <div>
                              <label htmlFor={idDateFin} className="mb-2 block text-xs font-semibold text-gray-700">
                                Date de fin
                              </label>
                              <input
                                id={idDateFin}
                                type="date"
                                value={dateFin}
                                onChange={(e) => setDateFin(e.target.value)}
                                min={dateDebut || borneDebutAnnee}
                                max={borneFinAnnee}
                                className={CHAMP}
                                required
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {scope !== 'single' && (
                  <div className="mt-4 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                    <div className="text-sm text-amber-800">
                      <strong>Attention :</strong> cette modification touchera{' '}
                      {scope === 'all' ? "tous les créneaux récurrents de l'année" : 'plusieurs créneaux'}.
                      Elle ne se défait pas.
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-4 lg:border-l lg:border-gray-200 lg:pl-6">
                <h3 className="text-sm font-semibold text-gray-700">Nouvelles valeurs</h3>

                <SearchableSelect
                  label="Enseignant"
                  value={formData.enseignant_id}
                  onChange={(value) => setFormData((prev) => ({ ...prev, enseignant_id: value }))}
                  options={teachers}
                  placeholder="Sélectionner un enseignant..."
                  searchKeys={['nom', 'prenom']}
                  required
                />

                <SearchableSelect
                  label="Matière"
                  value={formData.matiere_id}
                  onChange={(value) => setFormData((prev) => ({ ...prev, matiere_id: value }))}
                  options={subjects}
                  placeholder="Sélectionner une matière..."
                  searchKeys={['nom']}
                  required
                />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor={idDebut} className="mb-2 block text-sm font-semibold text-gray-700">
                      Heure de début
                    </label>
                    <input
                      id={idDebut}
                      type="time"
                      className={CHAMP}
                      value={formData.heure_debut}
                      onChange={(e) => setFormData((prev) => ({ ...prev, heure_debut: e.target.value }))}
                      min="07:00"
                      max="19:00"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor={idFin} className="mb-2 block text-sm font-semibold text-gray-700">
                      Heure de fin
                    </label>
                    <input
                      id={idFin}
                      type="time"
                      className={CHAMP}
                      value={formData.heure_fin}
                      onChange={(e) => setFormData((prev) => ({ ...prev, heure_fin: e.target.value }))}
                      min="07:00"
                      max="19:00"
                      required
                    />
                  </div>
                </div>

                {messageErreur && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>{messageErreur}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <Button type="button" variant="outline" className={BOUTON_PIED} onClick={handleClose}>
              Annuler
            </Button>
            <Button
              type="submit"
              className={`${BOUTON_PIED} border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a]`}
            >
              <Pencil className="h-4 w-4" />
              Enregistrer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditTimeSlotModal;
